import {
  Button,
  Center,
  Flex,
  Heading,
  Stack,
  Text,
  useBoolean,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import EnegeneLogo from '../../../components/EngeneLogo';
import KeycloakIcon from '../../../components/KeycloakIcon';
import StyledButton from '../../../components/StyledButton';
import { pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { EngeneLocation, useEngeneNavigate } from '../../../hooks/navigate';
import { useGetApplicationSettings } from '../../../hooks/queries/applicationSettings';
import { useInitialFetching } from '../../../hooks/queries/misc';
import useToastHook from '../../../hooks/ToastHook';
import { UserInfoAccessor } from '../../../utils/UserInfoUtils';

const Portal = () => {
  const { actions, state } = useGlobalStateMachine();
  const navigate = useEngeneNavigate();
  const location = useLocation() as EngeneLocation;
  const currentLocationState = location.state || { from: pagePaths.TOP };
  const breakpointValue = useBreakpointValue({ base: '20%', md: '30%' });
  const { toast } = useToastHook();
  const { keycloak } = useKeycloak();
  const [pushed, { off, on }] = useBoolean(false);

  const {
    data: applicationSettings,
    error,
    isError,
    isLoading,
  } = useGetApplicationSettings();
  React.useEffect(() => {
    if (!isLoading) {
      actions.updateAppConfig({
        ...state.appConfig,
        applicationSettings: Object.entries(
          applicationSettings?.results || {}
        ).reduce(
          (prev, [curK, curV]) => ({ ...prev, [curK]: JSON.parse(curV) }),
          {}
        ),
      });
    }
  }, [isLoading]);
  React.useEffect(() => {
    if (isError) {
      toast(
        error instanceof AppError
          ? error
          : 'アプリケーション設定の取得に失敗しました'
      );
    }
  }, [isError]);

  const initialFetching = useInitialFetching();

  if (pushed || !state.isLoggedIn) {
    // keycloak で認証中
  } else {
    // 認証済
    if (keycloak.authenticated) {
      const userInfo = UserInfoAccessor.setUserInfo();
      if (TypeUtils.isNil(userInfo)) {
        console.error('ユーザ情報を取得できませんでした');
      } else if (userInfo?.roleOptions.isEnabledToLogin) {
        if (initialFetching.isLoading) {
          console.info('初期データ取得中です');
        } else {
          initialFetching.mutate(null, {
            onSuccess: (results) => {
              if (TypeUtils.isNonNilArray(results)) {
                actions.setGlobalData(results);
                navigate(currentLocationState?.from as string);
              }
            },
          });
        }
      } else {
        toast('SE外注アプリにログインできません');
        console.error(`role_type ${userInfo?.role_type} はログインできません`);
        actions.logout();
      }
    }
    return (
      <Stack minH={'100vh'} direction={{ base: 'column', md: 'row' }}>
        <Flex p={8} flex={1} align={'center'} justify={'center'}>
          <Stack direction="column" spacing={6} w={'full'} maxW={'lg'}>
            <Text>
              認証中です。通信状況によっては時間がかかる場合がございます。
            </Text>
            <Text>
              30秒待ってもページに変化がない場合は、下のボタンをクリックしてください。
            </Text>
            <StyledButton onClick={() => actions.logout()}>戻る</StyledButton>
          </Stack>
        </Flex>
      </Stack>
    );
  }

  return (
    <Stack minH={'100vh'} direction={{ base: 'column', md: 'row' }}>
      <Flex p={8} flex={1} align={'center'} justify={'center'}>
        <Stack spacing={6} w={'full'} maxW={'lg'}>
          <Heading fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}>
            <Text
              as={'span'}
              position={'relative'}
              _after={{
                content: "''",
                width: 'full',
                height: breakpointValue,
                position: 'absolute',
                bottom: 1,
                left: 0,
                bg: 'blue.400',
                zIndex: -1,
              }}
            >
              for Craftsmen
            </Text>
            <Center p={2}>
              <EnegeneLogo width={240} height={55} />
            </Center>
          </Heading>
          <Text fontSize={{ base: 'md', lg: 'lg' }} color={'gray.500'}>
            WE WILL MAKE
            <br /> THE WORLD
            <br /> BETTER NOW <br />
            世界を今よりもっとよくする
          </Text>
          <Stack
            as="form"
            direction={{ base: 'column', md: 'row' }}
            onSubmit={(e) => {
              e.preventDefault();
              on();
              actions.login();
            }}
            spacing={4}
          >
            <Button
              w={'full'}
              rounded={'full'}
              bg={'blue.400'}
              color={'white'}
              _hover={{
                bg: 'blue.500',
              }}
              leftIcon={<KeycloakIcon />}
              type="submit"
            >
              Sign in with Keycloak
            </Button>
          </Stack>
        </Stack>
      </Flex>
    </Stack>
  );
};

export default Portal;
